/* eslint-disable @typescript-eslint/no-extra-parens */
const rfc3986 = (value: string): string =>
  encodeURIComponent(value).replace(/[!'()]/g, (c) =>
    '%' + c.charCodeAt(0).toString(16).toUpperCase()
  );

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

const randomNonce = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    try {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return toHex(bytes);
    } catch {
      // 某些 WebView 下 getRandomValues 不可用，降级到 Math.random
    }
  }
  let out = '';
  for (let i = 0; i < 8; i++) {
    out += Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  }
  return out;
};

const utf8Bytes = (value: string): number[] => {
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i++) {
    let code = value.charCodeAt(i);
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i++;
        bytes.push(
          0xf0 | (code >> 18),
          0x80 | ((code >> 12) & 0x3f),
          0x80 | ((code >> 6) & 0x3f),
          0x80 | (code & 0x3f)
        );
      } else {
        bytes.push(0xef, 0xbf, 0xbd);
      }
    } else {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  }
  return bytes;
};

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const sha256Hex = (bytes: number[]): string => {
  const H = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const bitLen = bytes.length * 8;
  const padded = bytes.slice();
  padded.push(0x80);
  while (padded.length % 64 !== 56) {
    padded.push(0x00);
  }
  const hi = Math.floor(bitLen / 0x100000000);
  const lo = bitLen >>> 0;
  padded.push(
    (hi >>> 24) & 0xff, (hi >>> 16) & 0xff, (hi >>> 8) & 0xff, hi & 0xff,
    (lo >>> 24) & 0xff, (lo >>> 16) & 0xff, (lo >>> 8) & 0xff, lo & 0xff
  );

  const W = new Array<number>(64);
  const M: number[] = [];
  for (let i = 0; i < padded.length; i += 4) {
    M.push(
      ((padded[i] & 0xff) << 24) | ((padded[i + 1] & 0xff) << 16) |
      ((padded[i + 2] & 0xff) << 8) | (padded[i + 3] & 0xff)
    );
  }
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  for (let t = 0; t < M.length; t += 16) {
    let a = H[0], b = H[1], c = H[2], d = H[3], e = H[4], f = H[5], g = H[6], h = H[7];
    for (let i = 0; i < 64; i++) {
      let w: number;
      if (i < 16) {
        w = M[t + i];
      } else {
        const s0 = rotr(W[i - 15], 7) ^ rotr(W[i - 15], 18) ^ (W[i - 15] >>> 3);
        const s1 = rotr(W[i - 2], 17) ^ rotr(W[i - 2], 19) ^ (W[i - 2] >>> 10);
        w = (W[i - 16] + s0 + W[i - 7] + s1) | 0;
      }
      W[i] = w;
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + SHA256_K[i] + w) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + temp1) | 0;
      d = c; c = b; b = a; a = (temp1 + temp2) | 0;
    }
    H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
    H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
  }
  let hex = '';
  for (let i = 0; i < 8; i++) {
    hex += (H[i] >>> 0).toString(16).padStart(8, '0');
  }
  return hex;
};

const hmacSha256HexFallback = (secret: string, message: string): string => {
  const blockSize = 64;
  let keyBytes = utf8Bytes(secret);
  if (keyBytes.length > blockSize) {
    const hashed = sha256Hex(keyBytes);
    keyBytes = [];
    for (let i = 0; i < hashed.length; i += 2) {
      keyBytes.push(parseInt(hashed.substr(i, 2), 16));
    }
  }
  while (keyBytes.length < blockSize) {
    keyBytes.push(0);
  }
  const oPad = keyBytes.map((b) => b ^ 0x5c);
  const iPad = keyBytes.map((b) => b ^ 0x36);
  const msgBytes = utf8Bytes(message);
  const innerHex = sha256Hex(iPad.concat(msgBytes));
  const innerBytes: number[] = [];
  for (let i = 0; i < innerHex.length; i += 2) {
    innerBytes.push(parseInt(innerHex.substr(i, 2), 16));
  }
  return sha256Hex(oPad.concat(innerBytes));
};

const hmacSha256Hex = async (secret: string, message: string): Promise<string> => {
  if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
    try {
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
      return toHex(new Uint8Array(signature));
    } catch {
      // Telegram iOS WebView 等环境下 crypto.subtle 可能不可用，降级到纯 JS 实现
    }
  }
  return hmacSha256HexFallback(secret, message);
};

export const signQuery = async (
  params: Record<string, string>,
  secret: string
): Promise<Record<string, string>> => {
  const signed: Record<string, string> = {
    ...params,
    ts: String(Math.floor(Date.now() / 1000)),
    nonce: randomNonce(),
  };
  const canonical = Object.keys(signed)
    .filter((key) => key !== 'sig')
    .sort()
    .map((key) => `${rfc3986(key)}=${rfc3986(signed[key])}`)
    .join('&');
  return {
    ...signed,
    sig: await hmacSha256Hex(secret, canonical),
  };
};
